import { createMouse } from 'mineflayer-mouse'
import { Bot } from 'mineflayer'
import { Block } from 'prismarine-block'
import { Vec3 } from 'vec3'
import { getThreeJsRendererMethods } from 'renderer/viewer/three/threeJsMethods'
import { isGameActive, showModal } from '../../globalState'
import { lastConnectOptions } from '../../appStatus'

import { isCypress } from '../../standaloneUtils'
import { playerState } from '../playerState'
import { sendVideoInteraction, videoCursorInteraction } from '../../customChannels'

const isPacketsReplaySession = () => !!lastConnectOptions.value?.worldStateFileContents

function cursorBlockDisplay (bot: Bot) {
  const updateCursorBlock = (data?: { block: Block }) => {
    if (!data?.block || bot.game.gameMode === 'spectator') {
      playerState.reactive.lookingAtBlock = undefined
      return
    }

    const { block } = data
    playerState.reactive.lookingAtBlock = {
      x: block.position.x,
      y: block.position.y,
      z: block.position.z,
      shapes: bot.mouse.getBlockCursorShapes(block).map(shape => {
        return bot.mouse.getDataFromShape(shape)
      })
    }
  }

  bot.on('highlightCursorBlock', updateCursorBlock)
  bot.on('game', () => {
    const block = bot.mouse.getCursorState().cursorBlock
    updateCursorBlock(block ? { block } : undefined)
  })

  bot.on('blockBreakProgressStage', (block, stage) => {
    const mergedShape = bot.mouse.getMergedCursorShape(block)
    playerState.reactive.diggingBlock = stage === null ? undefined : {
      x: block.position.x,
      y: block.position.y,
      z: block.position.z,
      stage,
      mergedShape: mergedShape ? bot.mouse.getDataFromShape(mergedShape) : undefined
    }
  })

  // Bridge server-side block_break_animation packets (used in replay mode)
  // to the diggingBlock reactive state so the crack overlay renders
  bot.on('blockBreakProgressObserved' as any, (block, destroyStage, _entity) => {
    if (!block) return
    const mergedShape = bot.mouse.getMergedCursorShape(block)
    playerState.reactive.diggingBlock = {
      x: block.position.x,
      y: block.position.y,
      z: block.position.z,
      stage: destroyStage,
      mergedShape: mergedShape ? bot.mouse.getDataFromShape(mergedShape) : undefined
    }
  })

  bot.on('blockBreakProgressEnd' as any, (block, _entity) => {
    if (!block) return
    if (playerState.reactive.diggingBlock &&
      playerState.reactive.diggingBlock.x === block.position.x &&
      playerState.reactive.diggingBlock.y === block.position.y &&
      playerState.reactive.diggingBlock.z === block.position.z) {
      playerState.reactive.diggingBlock = undefined
    }
  })

  // Fallback for replay: some packets arrive before chunks are loaded, so bot.blockAt() returns null.
  // Listen directly to raw packets and still show a crack overlay using a unit-cube shape.
  bot._client.on('block_break_animation', (packet: any) => {
    const { destroyStage, location } = packet ?? {}
    console.log('[BREAK-DEBUG] block_break_animation packet received:', JSON.stringify({ destroyStage, location }))
    if (!location) return

    const blockPos = new Vec3(location.x, location.y, location.z)
    if (destroyStage < 0 || destroyStage > 9) {
      if (playerState.reactive.diggingBlock &&
        playerState.reactive.diggingBlock.x === blockPos.x &&
        playerState.reactive.diggingBlock.y === blockPos.y &&
        playerState.reactive.diggingBlock.z === blockPos.z) {
        playerState.reactive.diggingBlock = undefined
      }
      return
    }

    const block = bot.blockAt(blockPos)
    const mergedShape = block ? bot.mouse.getMergedCursorShape(block) : undefined
    playerState.reactive.diggingBlock = {
      x: blockPos.x,
      y: blockPos.y,
      z: blockPos.z,
      stage: destroyStage,
      mergedShape: mergedShape ? bot.mouse.getDataFromShape(mergedShape) : {
        position: new Vec3(0, 0, 0),
        width: 1,
        height: 1,
        depth: 1
      }
    }
  })
}

export default (bot: Bot) => {
  bot.loadPlugin(createMouse({}))

  domListeners(bot)
  cursorBlockDisplay(bot)

  otherListeners()
}

const otherListeners = () => {
  bot.on('startDigging', (block) => {
    customEvents.emit('digStart')
  })

  bot.on('goingToSleep', () => {
    showModal({ reactType: 'bed' })
  })

  bot.on('botArmSwingStart', (hand) => {
    getThreeJsRendererMethods()?.changeHandSwingingState(true, hand === 'left')
  })

  bot.on('botArmSwingEnd', (hand) => {
    getThreeJsRendererMethods()?.changeHandSwingingState(false, hand === 'left')
  })

  bot.on('startUsingItem', (item, slot, isOffhand, duration) => {
    customEvents.emit('activateItem', item, isOffhand ? 45 : bot.quickBarSlot, isOffhand)
    playerState.startUsingItem()
  })

  bot.on('stopUsingItem', () => {
    playerState.stopUsingItem()
  })
}

const domListeners = (bot: Bot) => {
  const abortController = new AbortController()
  document.addEventListener('mousedown', (e) => {
    if (e.isTrusted && !document.pointerLockElement && !isCypress()) return
    if (!isGameActive(true)) return
    if (isPacketsReplaySession()) return

    getThreeJsRendererMethods()?.onPageInteraction()

    const videoInteraction = videoCursorInteraction()
    if (videoInteraction) {
      sendVideoInteraction(videoInteraction.id, videoInteraction.x, videoInteraction.y, e.button === 0)
      return
    }

    if (e.button === 0) {
      bot.leftClickStart()
    } else if (e.button === 2) {
      bot.rightClickStart()
    }
  }, { signal: abortController.signal })

  document.addEventListener('mouseup', (e) => {
    if (isPacketsReplaySession()) return
    if (e.button === 0) {
      bot.leftClickEnd()
    } else if (e.button === 2) {
      bot.rightClickEnd()
    }
  }, { signal: abortController.signal })

  bot.mouse.beforeUpdateChecks = () => {
    if (!document.hasFocus() || !isGameActive(true)) {
      // deactive all buttons
      bot.mouse.buttons.fill(false)
    }
  }

  bot.on('end', () => {
    abortController.abort()
  })
}
